import { Fragment, forwardRef, useState } from 'react'
import type { List, Map } from 'immutable'
import { camelCase } from 'lodash-es'
import { formField } from '@epages/react-components'

import { Popover } from '../Popover'
import { useTranslate } from '../../utils/translate'

interface Font extends Map<string, any> {
  get(key: 'group' | 'genericName'): string
  get(key: 'fonts'): List<string>
}

interface FontFieldRawProps {
  readonly fonts: List<Font>
  readonly onChange: (value: string) => void
  readonly scopedName: string
  readonly value: string
  readonly resetValue?: string
}

// Gets the first font name from a valid CSS `font-family` list value.
// Example: `"Open Sans", sans-serif` => `Open Sans`
function parseFontName(value: string): string | null {
  const regex = /"?([^,"]+)"?/
  const match = regex.exec(value)
  return match?.[1] ?? null
}

function getFontValue(name: string, font: Font): string {
  return [/\s/.test(name) ? `"${name}"` : name, font.get('genericName')].join(',')
}

// Use `ForwardRef` to avoid "Function components cannot be given refs"
// React warning (which is caused by the `react-components` "formField" HOC).
export const FontFieldRaw = forwardRef<HTMLDivElement, FontFieldRawProps>(
  ({ fonts, onChange, scopedName, value, resetValue }, _ref) => {
    const t = useTranslate('interface')
    const [searchValue, setSearchValue] = useState('')
    const scopedT = (key: string) => t(`components.fontFieldComponent.${key}`)
    const [isOpen, setIsOpen] = useState(false)
    const [initialValue, setInitialValue] = useState(value)

    const filteredFonts =
      searchValue?.length > 1
        ? fonts
            .map((font: Font) =>
              font.update('fonts', (fonts: List<string>) =>
                fonts.filter((name: string) => name.toLowerCase().includes(searchValue.toLowerCase())),
              ),
            )
            .filter((font: Font) => font.get('fonts').size > 0)
        : fonts

    const handleReset = () => resetValue && onChange(resetValue)

    const handleOpenChange = (open: boolean) => {
      if (open) {
        setInitialValue(value)
      }
      setIsOpen(open)
    }

    const handleEscapeKeyDown = () => {
      onChange(initialValue)
      setIsOpen(false)
    }

    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === 'Enter') {
        setIsOpen(false)
      }
    }

    return (
      <div className="ep-font-field">
        <Popover
          avoidCollisions
          className="ep-font-settings"
          side="left"
          sideOffset={40}
          open={isOpen}
          onOpenChange={handleOpenChange}
          onEscapeKeyDown={handleEscapeKeyDown}
          onKeyDown={handleKeyDown}
          anchor={
            <div className="ep-font-field-preview">
              <div>{scopedT('preview')}</div>
              <div className="ep-font-field-preview-selected" style={{ fontFamily: value }}>
                {scopedT('previewText')}
              </div>
              <div>
                <span>{scopedT('font')} </span>
                <span>{parseFontName(value)}</span>
              </div>
            </div>
          }
          trigger={
            <div className="ep-font-field-settings-button">
              <button className="ep-font-field-settings-button-change" type="button">
                {scopedT('changeFontButton.label')}
              </button>
            </div>
          }
        >
          <>
            <div className="ep-font-search-input-wrapper">
              <input
                className="ep-form-text-field ep-font-search-input"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder={scopedT('fontSelectionPopover.searchInputField.placeholder')}
              />
            </div>
            {filteredFonts.size === 0 ? (
              <div className="ep-font-search-no-result">
                {scopedT('fontSelectionPopover.searchInputField.noFontsText')}
              </div>
            ) : (
              <scroll-shadow>
                <div className="ep-font-settings-select">
                  {filteredFonts.toArray().map((font) => (
                    <fieldset key={`font-group-${font.get('group')}`}>
                      <legend>{scopedT(`fontSelectionPopover.fontTypes.${camelCase(font.get('group'))}`)}</legend>
                      {font.get('fonts').map((name) => {
                        const id = `${scopedName}-${font.get('group')}-${name}`
                        return (
                          <Fragment key={`font-${name}`}>
                            <input
                              id={id}
                              name={scopedName}
                              type="radio"
                              value={name}
                              checked={name === parseFontName(value)}
                              onChange={() => onChange(getFontValue(name, font))}
                            />
                            <label htmlFor={id} style={{ fontFamily: getFontValue(name, font) }}>
                              {name}
                            </label>
                          </Fragment>
                        )
                      })}
                    </fieldset>
                  ))}
                </div>
              </scroll-shadow>
            )}
            {resetValue && filteredFonts.size > 0 && (
              <button className="ep-font-settings-reset-button" type="button" onClick={handleReset}>
                {scopedT('fontSelectionPopover.resetButton.label')}
              </button>
            )}
          </>
        </Popover>
      </div>
    )
  },
)

FontFieldRaw.displayName = 'FontFieldRaw'

export default formField()(FontFieldRaw)
